import { collectConditions, isGroup, OPERATORS_BY_TYPE } from './rules';
import {
  DISPLAY_TYPES,
  FIELD_TYPES,
  MAPS_TO,
  OPERATORS,
  type Condition,
  type FieldType,
  type FormField,
  type MapsTo,
  type Rule,
} from './types';

export interface DefinitionIssue {
  level: 'error' | 'warning';
  field?: string;
  code: string;
  message: string;
}

export interface DefinitionReport {
  ok: boolean;
  errors: DefinitionIssue[];
  warnings: DefinitionIssue[];
  /** lookup codes referenced by conditions — the backend checks they exist and are active */
  referencedCodes: { field: string; fieldType: FieldType; codes: string[] }[];
  /** countries a location field limits itself to (ISO codes) — the backend checks they exist */
  referencedCountries: { field: string; codes: string[] }[];
}

/** Which field types may fill which system column. */
export const MAPS_TO_TYPES: Record<MapsTo, FieldType[]> = {
  request_type: ['request_type'],
  category: ['property_category'],
  property_type: ['property_type'],
  features: ['property_features'],
  location: ['location'],
  coordinates: ['map_point'],
  price: ['number', 'currency'],
  price_range: ['number_range'],
  area: ['number'],
  area_range: ['number_range'],
  building_area: ['number'],
  land_area: ['number'],
  outer_area: ['number'],
  inner_area_range: ['number_range'],
  outer_area_range: ['number_range'],
  land_area_range: ['number_range'],
  building_area_range: ['number_range'],
  rooms: ['number'],
  bathrooms: ['number'],
  floor: ['number'],
  contact_name: ['text'],
  contact_phone: ['phone'],
  contact_email: ['email'],
  images: ['image_upload'],
  notes: ['textarea', 'text'],
};

const LOOKUP_TYPES: ReadonlySet<FieldType> = new Set(['request_type', 'property_category', 'property_type', 'property_features']);
const KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;

function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : v === undefined || v === null ? [] : [v];
}

function checkRuleShape(rule: Rule, path: string, issues: DefinitionIssue[], field: string): void {
  if (typeof rule !== 'object' || rule === null) {
    issues.push({ level: 'error', field, code: 'RULE_INVALID', message: `شرط غير صالح في ${path}` });
    return;
  }
  if (!isGroup(rule)) return;
  const g = rule;
  const keys = Object.keys(g);
  if (keys.length === 0 || keys.some((k) => !['all', 'any', 'not'].includes(k))) {
    issues.push({ level: 'error', field, code: 'RULE_INVALID', message: `مجموعة شروط غير صالحة في ${path}` });
  }
  g.all?.forEach((r, i) => checkRuleShape(r, `${path}.all[${i}]`, issues, field));
  g.any?.forEach((r, i) => checkRuleShape(r, `${path}.any[${i}]`, issues, field));
  if (g.not) checkRuleShape(g.not, `${path}.not`, issues, field);
}

/** Detect obviously contradictory `all` groups: same field required to equal two different values. */
function neverTrue(rule: Rule | null | undefined): boolean {
  if (!rule || !isGroup(rule) || !rule.all) return false;
  const eqs = new Map<string, Set<string>>();
  for (const r of rule.all) {
    if (isGroup(r)) {
      if (neverTrue(r)) return true;
      continue;
    }
    const allowed =
      r.op === 'eq' ? [String(r.value)] : r.op === 'in' ? asArray(r.value).map(String) : null;
    if (!allowed) continue;
    const prev = eqs.get(r.field);
    if (!prev) eqs.set(r.field, new Set(allowed));
    else {
      const inter = new Set([...prev].filter((x) => allowed.includes(x)));
      if (inter.size === 0) return true;
      eqs.set(r.field, inter);
    }
  }
  return false;
}

/**
 * Validate a whole form definition before publishing (spec §6.7).
 * Pure: lookup-code existence is returned in `referencedCodes` for the backend to verify.
 */
export function validateDefinition(input: FormField[]): DefinitionReport {
  const issues: DefinitionIssue[] = [];
  const fields = [...input].sort((a, b) => a.order - b.order);
  const byKey = new Map<string, { field: FormField; index: number }>();
  const referenced = new Map<string, { fieldType: FieldType; codes: Set<string> }>();
  const countries: { field: string; codes: string[] }[] = [];

  // --- structure --------------------------------------------------------------------------
  if (fields.length === 0) {
    issues.push({ level: 'error', code: 'FORM_EMPTY', message: 'النموذج لا يحتوي أي حقل' });
  }
  const orders = new Set<number>();
  fields.forEach((f, index) => {
    if (!KEY_RE.test(f.key)) issues.push({ level: 'error', field: f.key, code: 'KEY_INVALID', message: 'مفتاح الحقل يجب أن يكون snake_case بالإنجليزية' });
    if (byKey.has(f.key)) issues.push({ level: 'error', field: f.key, code: 'KEY_DUPLICATE', message: 'مفتاح الحقل مكرر' });
    byKey.set(f.key, { field: f, index });
    if (orders.has(f.order)) issues.push({ level: 'error', field: f.key, code: 'ORDER_DUPLICATE', message: 'ترتيب الحقل مكرر' });
    orders.add(f.order);
    if (!FIELD_TYPES.includes(f.type)) issues.push({ level: 'error', field: f.key, code: 'TYPE_INVALID', message: 'نوع الحقل غير معروف' });
    if (!f.label?.ar?.trim()) issues.push({ level: 'error', field: f.key, code: 'LABEL_REQUIRED', message: 'عنوان الحقل بالعربية مطلوب' });
  });

  const first = fields[0];
  if (first && (first.type !== 'request_type' || !first.required)) {
    issues.push({ level: 'error', field: first.key, code: 'REQUEST_TYPE_FIRST', message: 'أول حقل يجب أن يكون "نوع الطلب" وإلزامياً' });
  }
  const requestTypeFields = fields.filter((f) => f.type === 'request_type');
  if (requestTypeFields.length !== 1) {
    issues.push({ level: 'error', code: 'REQUEST_TYPE_COUNT', message: 'يجب وجود حقل "نوع الطلب" واحد فقط' });
  }
  if (requestTypeFields[0] && requestTypeFields[0].visibleWhen) {
    issues.push({ level: 'error', field: requestTypeFields[0].key, code: 'REQUEST_TYPE_CONDITIONAL', message: 'حقل نوع الطلب لا يمكن أن يكون مشروطاً' });
  }

  // --- maps_to ------------------------------------------------------------------------------
  const mapped = new Map<string, string>();
  for (const f of fields) {
    if (!f.mapsTo) continue;
    if (!MAPS_TO.includes(f.mapsTo)) {
      issues.push({ level: 'error', field: f.key, code: 'MAPS_TO_INVALID', message: 'ربط غير معروف' });
      continue;
    }
    if (DISPLAY_TYPES.has(f.type)) issues.push({ level: 'error', field: f.key, code: 'MAPS_TO_DISPLAY', message: 'حقل العرض لا يُربط بعمود' });
    if (!MAPS_TO_TYPES[f.mapsTo].includes(f.type)) {
      issues.push({ level: 'error', field: f.key, code: 'MAPS_TO_TYPE', message: `الربط ${f.mapsTo} لا يتوافق مع نوع الحقل ${f.type}` });
    }
    if (mapped.has(f.mapsTo)) issues.push({ level: 'error', field: f.key, code: 'MAPS_TO_DUPLICATE', message: `الربط ${f.mapsTo} مستخدم في الحقل ${mapped.get(f.mapsTo)}` });
    mapped.set(f.mapsTo, f.key);
  }
  for (const [needed, type] of [['request_type', 'request_type'], ['contact_phone', 'phone'], ['contact_name', 'text']] as const) {
    const f = fields.find((x) => x.mapsTo === needed);
    if (!f) issues.push({ level: 'error', code: 'SYSTEM_FIELD_MISSING', message: `الحقل النظامي ${needed} (${type}) مطلوب` });
    else if (!f.required || f.visibleWhen) issues.push({ level: 'error', field: f.key, code: 'SYSTEM_FIELD_OPTIONAL', message: `الحقل ${needed} يجب أن يكون إلزامياً وظاهراً دائماً` });
  }
  const ptype = fields.find((f) => f.type === 'property_type');
  const pcat = fields.find((f) => f.type === 'property_category');
  if (ptype && !pcat) issues.push({ level: 'error', field: ptype.key, code: 'CATEGORY_MISSING', message: 'حقل نوع العقار يحتاج حقل فئة العقار' });
  if (ptype && pcat && byKey.get(pcat.key)!.index > byKey.get(ptype.key)!.index) {
    issues.push({ level: 'error', field: ptype.key, code: 'CATEGORY_ORDER', message: 'حقل الفئة يجب أن يسبق حقل نوع العقار' });
  }

  // --- config -------------------------------------------------------------------------------
  for (const f of fields) {
    const v = f.config?.validation;
    if (v?.min !== undefined && v?.max !== undefined && v.min > v.max) {
      issues.push({ level: 'error', field: f.key, code: 'MIN_MAX', message: 'الحد الأدنى أكبر من الحد الأعلى' });
    }
    if (v?.minLength !== undefined && v?.maxLength !== undefined && v.minLength > v.maxLength) {
      issues.push({ level: 'error', field: f.key, code: 'MIN_MAX', message: 'أقل طول أكبر من أقصى طول' });
    }
    if (v?.pattern) {
      try {
        new RegExp(v.pattern);
      } catch {
        issues.push({ level: 'error', field: f.key, code: 'PATTERN_INVALID', message: 'تعبير النمط غير صالح' });
      }
    }
    if (['radio', 'dropdown', 'checkbox_group'].includes(f.type)) {
      const opts = f.config?.options ?? [];
      if (opts.length === 0) issues.push({ level: 'error', field: f.key, code: 'OPTIONS_REQUIRED', message: 'الحقل يحتاج خيارات' });
      const vals = opts.map((o) => o.value);
      if (new Set(vals).size !== vals.length) issues.push({ level: 'error', field: f.key, code: 'OPTIONS_DUPLICATE', message: 'قيم الخيارات مكررة' });
      if (opts.some((o) => !o.label?.ar)) issues.push({ level: 'error', field: f.key, code: 'OPTION_LABEL', message: 'كل خيار يحتاج عنواناً بالعربية' });
    }
    // request types this form offers: lookup codes, checked by the backend like the ones in conditions
    if (f.type === 'request_type' && (f.config?.options?.length ?? 0) > 0) {
      const entry = referenced.get(f.key) ?? { fieldType: f.type, codes: new Set<string>() };
      f.config.options!.forEach((o) => entry.codes.add(String(o.value)));
      referenced.set(f.key, entry);
    }
    if (f.type === 'location' && f.config?.countries !== undefined) {
      const list = f.config.countries;
      if (!Array.isArray(list) || list.some((c) => typeof c !== 'string' || !/^[A-Z]{2}$/.test(c))) {
        issues.push({ level: 'error', field: f.key, code: 'COUNTRIES_INVALID', message: 'قائمة الدول غير صالحة (رموز ISO مثل JO)' });
      } else if (list.length) countries.push({ field: f.key, codes: [...new Set(list)] });
    }
    if (DISPLAY_TYPES.has(f.type) && f.required) {
      issues.push({ level: 'error', field: f.key, code: 'DISPLAY_REQUIRED', message: 'حقل العرض لا يكون إلزامياً' });
    }
  }

  // --- conditions (the field's own, and its options') ----------------------------------------
  for (const f of fields) {
    const rules: [string, Rule | null | undefined][] = [
      ['visibleWhen', f.visibleWhen],
      ['requiredWhen', f.requiredWhen],
      ...(f.config?.options ?? []).filter((o) => o.visibleWhen).map((o): [string, Rule] => [`option ${o.value}`, o.visibleWhen!]),
    ];
    for (const [prop, rule] of rules) {
      if (!rule) continue;
      checkRuleShape(rule, prop, issues, f.key);
      const selfIndex = byKey.get(f.key)!.index;
      for (const c of collectConditions(rule) as Condition[]) {
        const ref = byKey.get(c.field);
        if (!ref) {
          issues.push({ level: 'error', field: f.key, code: 'RULE_UNKNOWN_FIELD', message: `الشرط يشير لحقل غير موجود: ${c.field}` });
          continue;
        }
        if (ref.index >= selfIndex) {
          issues.push({ level: 'error', field: f.key, code: 'RULE_FORWARD_REF', message: `الشرط يجب أن يعتمد على حقل سابق، و"${c.field}" يأتي بعده` });
        }
        if (DISPLAY_TYPES.has(ref.field.type)) {
          issues.push({ level: 'error', field: f.key, code: 'RULE_DISPLAY_REF', message: `لا يمكن الاعتماد على حقل عرض: ${c.field}` });
          continue;
        }
        if (!OPERATORS.includes(c.op)) {
          issues.push({ level: 'error', field: f.key, code: 'RULE_OPERATOR', message: `عامل غير معروف: ${String(c.op)}` });
          continue;
        }
        if (!OPERATORS_BY_TYPE[ref.field.type].includes(c.op)) {
          issues.push({ level: 'error', field: f.key, code: 'RULE_OPERATOR_TYPE', message: `العامل ${c.op} لا يناسب نوع الحقل ${ref.field.type}` });
        }
        if (!['is_empty', 'is_not_empty'].includes(c.op) && (c.value === undefined || c.value === null || (Array.isArray(c.value) && c.value.length === 0))) {
          issues.push({ level: 'error', field: f.key, code: 'RULE_VALUE', message: `الشرط على ${c.field} يحتاج قيمة` });
        }
        if (c.op === 'between' && (!Array.isArray(c.value) || c.value.length !== 2)) {
          issues.push({ level: 'error', field: f.key, code: 'RULE_VALUE', message: 'العامل between يحتاج قيمتين' });
        }
        // static options must exist (a request type outside the form's list is fine: the condition just never applies)
        const staticOpts = LOOKUP_TYPES.has(ref.field.type) ? undefined : ref.field.config?.options?.map((o) => o.value);
        if (staticOpts && staticOpts.length && !['is_empty', 'is_not_empty'].includes(c.op)) {
          const unknown = asArray(c.value).map(String).filter((v) => !staticOpts.includes(v));
          if (unknown.length) issues.push({ level: 'error', field: f.key, code: 'RULE_OPTION_UNKNOWN', message: `قيم غير موجودة في خيارات ${c.field}: ${unknown.join(', ')}` });
        }
        if (LOOKUP_TYPES.has(ref.field.type) && !['is_empty', 'is_not_empty'].includes(c.op)) {
          const entry = referenced.get(c.field) ?? { fieldType: ref.field.type, codes: new Set<string>() };
          asArray(c.value).forEach((v) => entry.codes.add(String(v)));
          referenced.set(c.field, entry);
        }
      }
      if (neverTrue(rule)) {
        issues.push({ level: 'warning', field: f.key, code: 'RULE_NEVER_TRUE', message: 'هذا الشرط متناقض — الحقل لن يظهر أبداً' });
      }
    }
  }

  const errors = issues.filter((i) => i.level === 'error');
  const warnings = issues.filter((i) => i.level === 'warning');
  return {
    ok: errors.length === 0,
    errors,
    warnings,
    referencedCodes: [...referenced.entries()].map(([field, e]) => ({ field, fieldType: e.fieldType, codes: [...e.codes] })),
    referencedCountries: countries,
  };
}

/** Human-readable Arabic sentence for a rule — used by the builder UI. */
export function describeRule(rule: Rule | null | undefined, labelOf: (key: string) => string = (k) => k): string {
  if (!rule) return 'يظهر دائماً';
  const OPS: Record<string, string> = {
    eq: '=', neq: '≠', in: 'ضمن', not_in: 'ليس ضمن', contains: 'يحتوي', contains_any: 'يحتوي أياً من',
    contains_all: 'يحتوي كل', not_contains: 'لا يحتوي', gt: '>', gte: '≥', lt: '<', lte: '≤', between: 'بين',
    is_empty: 'فارغ', is_not_empty: 'غير فارغ', location_in: 'ضمن الموقع',
  };
  const walk = (r: Rule): string => {
    if (!isGroup(r)) {
      const v = r.value === undefined ? '' : ` ${Array.isArray(r.value) ? `[${r.value.join('، ')}]` : String(r.value)}`;
      return `${labelOf(r.field)} ${OPS[r.op] ?? r.op}${v}`;
    }
    const parts: string[] = [];
    if (r.all?.length) parts.push(r.all.map(walk).join(' و '));
    if (r.any?.length) parts.push(`(${r.any.map(walk).join(' أو ')})`);
    if (r.not) parts.push(`ليس (${walk(r.not)})`);
    return parts.join(' و ');
  };
  return `يظهر إذا: ${walk(rule)}`;
}
